'use client';

import { useState } from 'react';
import { Eye, ShoppingCart, Images } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCart } from '@/contexts/CartContext';
import type { CatalogItem, CatalogType } from '@/lib/services/catalog/catalog-types';
import Image from 'next/image';
import { features } from '@/lib/config/features';
import { mayBeAnimated, stillLoader } from '@/lib/utils/media.utils';

interface CatalogCardProps {
  artwork: CatalogItem;
  onView?: (artwork: CatalogItem) => void;
  onEnlarge: (artwork: CatalogItem) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onCopyWallet?: (walletAddress: string | undefined) => void;
  copiedWallet?: string | null;
  catalogType?: CatalogType;
  galleryWall?: boolean;
  useAspectRatio?: boolean;
  index?: number;
  hidePurchaseButtons?: boolean;
  linkTarget?: '_blank' | '_self';
}

export function CatalogCard({
  artwork,
  onView,
  onEnlarge: _onEnlarge,
  onMouseEnter,
  onMouseLeave,
  onMouseMove,
  onCopyWallet: _onCopyWallet,
  copiedWallet: _copiedWallet,
  catalogType: _catalogType = 'all',
  galleryWall = false,
  useAspectRatio: _useAspectRatio = true,
  index: _index = 0,
  hidePurchaseButtons = false,
  linkTarget,
}: CatalogCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [hasBeenHovered, setHasBeenHovered] = useState(false);
  const { addToCart } = useCart();
  const router = useRouter();
  
  // Use transition type from artwork data, default to blur-dissolve
  const hoverTransition = artwork.hoverTransition || 'blur-dissolve';
  
  // Fixed values
  let artworkCount = 0;
  let collectorCount = 0;
  let showCollectionStats = false;

  switch (artwork.type) {
    case 'collection':
      artworkCount = artwork.artworkCount || 0;
      collectorCount = artwork.collectorCount || 0;
      showCollectionStats = true;
      break;
  }

  const additionalDivClass: React.CSSProperties = {};
  const additionalArtWorkClass: React.CSSProperties = {};
  if (galleryWall) {
    additionalDivClass['height'] = 'calc(var(--gallery-wall-height) - 64px)';
    additionalDivClass['minHeight'] = '0';
    additionalDivClass['minWidth'] = '0';
    additionalDivClass['width'] = '100%';
    additionalDivClass['position'] = `relative`;
    additionalDivClass['scrollSnapAlign'] = 'center'; // Make all images snap to center
    additionalDivClass['scrollSnapStop'] = 'always'; // Ensure it always stops at each snap point
    additionalDivClass['paddingBottom'] = 0;
    additionalArtWorkClass['height'] = '100%';
    additionalArtWorkClass['minHeight'] = '0';
    additionalArtWorkClass['display'] = 'block';
    additionalArtWorkClass['width'] = '100%';
    additionalArtWorkClass['objectFit'] = 'contain';
    additionalArtWorkClass['maxWidth'] = 'none';
    additionalArtWorkClass['position'] = 'relative';
  }

  const primaryCategory = artwork.category || artwork.categories?.[0] || null;
  const formattedPrice =
    typeof artwork.price === 'number'
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(artwork.price)
      : null;

  const handleViewDetails = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onView) {
      onView(artwork);
    } else if (artwork.href) {
      router.push(artwork.href);
    }
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (artwork.price) {
      addToCart({
        id: String(artwork.id),
        title: artwork.title,
        price: artwork.price,
        thumbnail: artwork.thumbnail,
      }, 1);
    }
  };

  const _handleBuyNow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (artwork.price) {
      addToCart({
        id: String(artwork.id),
        title: artwork.title,
        price: artwork.price,
        thumbnail: artwork.thumbnail,
      }, 1);
      window.location.href = '/store/checkout';
    }
  };

  const imageWidth = artwork.width ?? 1000;
  const imageHeight = artwork.height ?? 1000;
  const imageAspectRatio =
    artwork.width && artwork.height
      ? artwork.height / artwork.width
      : 1;

  const editionTag = artwork.tags?.find(tag => /^edition/i.test(tag)) || null;
  const editionCount = editionTag ? editionTag.replace(/edition\s*/i, '').trim() || '1 / 10' : '1 / 10';

  const actionButtonClass =
    'flex items-center justify-center h-6 px-2 rounded-md bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors';

  // A possibly animated thumbnail shows its still first frame, and plays on hover: a grid of
  // animations sent whole was 140 to 250 MB for one collection page.
  const animated = mayBeAnimated(artwork.thumbnail);
  const hoverSrc = artwork.imageUrl && artwork.imageUrl !== artwork.thumbnail ? artwork.imageUrl : animated ? artwork.thumbnail : '';
  const hasHoverImage = !!hoverSrc;
  const sizes = galleryWall ? '50vw' : '(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw';

  return (
    <Link
      href={artwork.href || '#'}
      target={linkTarget}
      className={galleryWall ? 'block cursor-pointer flex-none h-full' : 'block cursor-pointer'}
      onMouseEnter={() => {
        setIsHovered(true);
        if (!hasBeenHovered) setHasBeenHovered(true);
        if (onMouseEnter) onMouseEnter();
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        if (onMouseLeave) onMouseLeave();
      }}
      onMouseMove={onMouseMove}
    >
      <div
        className="masonry-item rounded-md overflow-hidden group bg-white"
        style={galleryWall ? { height: 'var(--gallery-wall-height)', minHeight: 0, width: `calc((var(--gallery-wall-height) - 64px) * ${imageWidth / imageHeight})` } : {}}
      >
        <div
          className="overflow-hidden relative"
          style={{
            position: 'relative',
            // Preserve the artwork's dimensions; unknown-size thumbnail frames use contain.
            paddingBottom: `${imageAspectRatio * 100}%`,
            ...additionalDivClass,
          }}
        >
          {hasHoverImage ? (
            <>
              {/* Default Image - Crossfade with Scale */}
              {hoverTransition === 'crossfade-scale' && (
                <>
                  <Image
                    src={artwork.thumbnail}
                    loader={animated ? stillLoader : undefined}
                    sizes={sizes}
                    alt={artwork.title}
                    className="top-0 left-0 w-full h-full object-contain rounded-md"
                    loading="lazy"
                    width={imageWidth}
                    height={imageHeight}
                    style={{
                      position: 'absolute',
                      objectPosition: 'center center',
                      opacity: isHovered ? 0 : 1,
                      transform: 'none',
                      transition: 'opacity 600ms cubic-bezier(0.4, 0, 0.2, 1), transform 600ms cubic-bezier(0.4, 0, 0.2, 1)',
                      willChange: hasBeenHovered ? 'opacity, transform' : 'auto',
                      ...additionalArtWorkClass,
                    }}
                  />
                  {/* Hover image - only rendered after first hover (lazy load) */}
                  {hasBeenHovered && (
                    <Image
                      src={hoverSrc}
                      unoptimized={mayBeAnimated(hoverSrc)}
                      sizes={sizes}
                      alt={artwork.title}
                      className="top-0 left-0 w-full h-full object-contain rounded-md"
                      loading="lazy"
                      width={imageWidth}
                      height={imageHeight}
                      style={{
                        position: 'absolute',
                        objectPosition: 'center center',
                        opacity: isHovered ? 1 : 0,
                        transform: 'none',
                        transition: 'opacity 600ms cubic-bezier(0.4, 0, 0.2, 1), transform 600ms cubic-bezier(0.4, 0, 0.2, 1)',
                        willChange: 'opacity, transform',
                        ...additionalArtWorkClass,
                      }}
                    />
                  )}
                </>
              )}

              {/* Blur Dissolve */}
              {hoverTransition === 'blur-dissolve' && (
                <>
                  <Image
                    src={artwork.thumbnail}
                    loader={animated ? stillLoader : undefined}
                    sizes={sizes}
                    alt={artwork.title}
                    className="top-0 left-0 w-full h-full object-contain rounded-md"
                    loading="lazy"
                    width={imageWidth}
                    height={imageHeight}
                    style={{
                      position: 'absolute',
                      objectPosition: 'center center',
                      opacity: isHovered ? 0 : 1,
                      filter: isHovered ? 'blur(4px)' : 'blur(0px)',
                      transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), filter 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                      willChange: hasBeenHovered ? 'opacity, filter' : 'auto',
                      ...additionalArtWorkClass,
                    }}
                  />
                  {/* Hover image - only rendered after first hover (lazy load) */}
                  {hasBeenHovered && (
                    <Image
                      src={hoverSrc}
                      unoptimized={mayBeAnimated(hoverSrc)}
                      sizes={sizes}
                      alt={artwork.title}
                      className="top-0 left-0 w-full h-full object-contain rounded-md"
                      loading="lazy"
                      width={imageWidth}
                      height={imageHeight}
                      style={{
                        position: 'absolute',
                        objectPosition: 'center center',
                        opacity: isHovered ? 1 : 0,
                        filter: isHovered ? 'blur(0px)' : 'blur(4px)',
                        transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), filter 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                        willChange: 'opacity, filter',
                        ...additionalArtWorkClass,
                      }}
                    />
                  )}
                </>
              )}
            </>
          ) : artwork.thumbnail ? (
            <Image
              src={artwork.thumbnail}
              sizes={sizes}
              alt={artwork.title}
              className="top-0 left-0 w-full h-full object-contain rounded-md"
              loading="lazy"
              width={imageWidth}
              height={imageHeight}
              style={{
                position: 'absolute',
                objectPosition: 'center center',
                ...additionalArtWorkClass,
              }}
            />
          ) : (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
              <span className="text-gray-400 text-[12px] font-mono">NO IMAGE</span>
            </div>
          )}
          {/* Katalog tipi etiketi */}
          {/* {typeTag.label && (
              <div className={`absolute top-2 right-2 ${typeTag.bgColor} ${typeTag.textColor} text-[12px] px-2 py-1 font-mono rounded-sm`}>
                {typeTag.label}
              </div>
            )} */}
          {artwork?.tags && artwork.tags.includes('UNFILTERED') && (
            <div className="absolute top-2 left-2 bg-zinc-200 text-zinc-800 text-[12px] px-2 py-1 font-mono">
              UNFILTERED
            </div>
          )}
        </div>
        <div className="p-0 mt-[7px]">
          <div className="flex justify-between items-start gap-2">
            <div className="flex flex-col gap-0.5 overflow-hidden flex-1">
              {primaryCategory && (
                <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-wide truncate">
                  {primaryCategory}
                </span>
              )}
              <p className="text-[10px] font-mono text-zinc-800 uppercase line-clamp-2">{artwork.title}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {/*
              æ
              <button
                className="w-5 h-5 flex items-center justify-center transition-all text-zinc-500 hover:text-zinc-800 -mt-[3px] -mr-[4px]"
                title="Enlarge artwork"
                onClick={e => {
                  e.stopPropagation();
                  onEnlarge(artwork);
                }}
              >
                <Maximize2 className="h-3 w-3 text-zinc-700" />
              </button>
             
              {onView && (
                <button
                  className="w-5 h-5 flex items-center justify-center transition-all text-zinc-500 hover:text-zinc-800 -mt-[3px] -mr-[4px]"
                  title="View details"
                  onClick={e => {
                    e.stopPropagation();
                    onView(artwork);
                  }}
                >
                  <Eye className="h-3 w-3 text-zinc-700" />
                </button>
              )}
              */}
              <button
                type="button"
                className={`${actionButtonClass} mr-0`}
                title="View details"
                aria-label="View details"
                onClick={handleViewDetails}
              >
                <Eye className="h-3 w-3" />
              </button>
              {features.store && !hidePurchaseButtons && (
                <button
                  type="button"
                  className={actionButtonClass}
                  title="Add to cart"
                  aria-label="Add to cart"
                  onClick={handleAddToCart}
                >
                  <ShoppingCart className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {features.store && !hidePurchaseButtons && (
            <div className="flex items-center justify-between mt-1">
              <span className="text-[11px] font-mono text-zinc-500 normal-case">
                {formattedPrice ?? ''}
              </span>
              <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wide text-zinc-600">
                <Images className="h-3 w-3" />
                <span>{editionCount}</span>
              </span>
            </div>
          )}

          {/* Collection Statistics - Only show for collection type on client side */}
          {showCollectionStats && (
            <div className="flex flex-wrap gap-1 mt-1">
              {artworkCount > 0 && (
                <span className="text-[8px] px-1 py-0.5 font-mono bg-zinc-200 text-zinc-700 rounded-sm">
                  {artworkCount} ARTWORKS
                </span>
              )}
              {collectorCount > 0 && (
                <span className="text-[8px] px-1 py-0.5 font-mono bg-zinc-200 text-zinc-700 rounded-sm">
                  {collectorCount} COLLECTORS
                </span>
              )}
            </div>
          )}

          {/* Tags removed */}
        </div>
      </div>
    </Link>
  );
}
